import { InfoCircleOutlined } from '@ant-design/icons'
import React from 'react'
import styled from 'styled-components'
import { Button } from 'sutra/components/Button'
import { Sparkle } from 'sutra/components/Icons'
import { TextArea } from 'sutra/components/Input/TextArea'
import { Popover } from 'sutra/components/Popover/Popover'
import { Text } from 'sutra/components/Text/Text'
import { gray } from 'sutra/tokens/colors'
import { spacing } from 'sutra/tokens/spacing'
import { typography } from 'sutra/tokens/typography'

import type { FilterControlProps } from 'features/charts-v2'
import {
  AGENT_DESCRIPTION_LABEL,
  AGENT_DESCRIPTION_PLACEHOLDER,
  AGENT_DESCRIPTION_TIME_RANGE_WARNING,
  SENSITIVITY_HELPER_TEXT,
  containsAgentDescriptionTimeRange,
} from 'features/trend-detection/constants'
import { AgentFilterPanel } from 'features/trend-detection/features/agent-create/components/AgentFilterPanel'
import { SensitivityControl } from 'features/trend-detection/features/agent-create/components/SensitivityControl'

/** Bag of props forwarded to `AgentFilterPanel`. Bundled so this component
 *  doesn't restate each filter-panel field as its own prop. */
export interface IssueAgentFilterPanelBag {
  filterControlProps: FilterControlProps
  /** Rendered below the filter cards inside the panel — the stale-filters
   * notice, or any similar warning the container decides to surface. */
  staleFiltersNotice?: React.ReactNode
}

export interface IssueAgentFieldsProps {
  prompt: string
  onPromptChange: (value: string) => void
  onSuggestFilters: () => void
  generatingFilters: boolean
  sensitivity: string
  onSensitivityChange: (value: string) => void
  filterPanel: IssueAgentFilterPanelBag
}

/** Issue Agent's Instructions body: monitor prompt → suggest-filters
 * affordance → filter cards → sensitivity. Pure view — all filter-panel
 * wiring happens upstream in `use-agent-filter-panel` (Container-owned). */
export const IssueAgentFields: React.FC<IssueAgentFieldsProps> = ({
  prompt,
  onPromptChange,
  onSuggestFilters,
  generatingFilters,
  sensitivity,
  onSensitivityChange,
  filterPanel,
}) => {
  const showsTimeRangeWarning = containsAgentDescriptionTimeRange(prompt)

  return (
    <Fields>
      <Field>
        <Label>{AGENT_DESCRIPTION_LABEL}</Label>
        <TextArea
          rows={4}
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          placeholder={AGENT_DESCRIPTION_PLACEHOLDER}
        />
        {showsTimeRangeWarning && (
          <Text $size="xs" as="span">
            {AGENT_DESCRIPTION_TIME_RANGE_WARNING}
          </Text>
        )}
        <SuggestButton
          variant="secondary"
          size="sm"
          onClick={onSuggestFilters}
          disabled={generatingFilters || !prompt.trim()}
        >
          <Sparkle />
          {generatingFilters ? 'Suggesting…' : 'Suggest filters'}
        </SuggestButton>
      </Field>

      <Field>
        <AgentFilterPanel
          headerLabel={<Label>Filters</Label>}
          filterControlProps={filterPanel.filterControlProps}
          isSuggesting={generatingFilters}
          staleFiltersNotice={filterPanel.staleFiltersNotice}
        />
      </Field>

      <Field>
        <LabelRow>
          <Label>Flag issues that are...</Label>
          <Popover
            openOnHover
            nativeButton={false}
            initialFocus={false}
            content={<HelpContent>{SENSITIVITY_HELPER_TEXT}</HelpContent>}
          >
            <HelpIcon aria-label="How sensitivity works">
              <InfoCircleOutlined />
            </HelpIcon>
          </Popover>
        </LabelRow>
        <SensitivityControl
          value={sensitivity}
          onChange={onSensitivityChange}
        />
      </Field>
    </Fields>
  )
}

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[8]}px;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]}px;
`

const Label = styled.label`
  font-family: ${typography.fontFamily};
  font-size: 13px;
  font-weight: ${typography.weights.semibold};
  color: ${gray[900]};
`

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[1]}px;
`

const HelpIcon = styled.span`
  display: inline-flex;
  color: ${gray[500]};
  cursor: help;
  font-size: 13px;

  &:hover {
    color: ${gray[700]};
  }
`

const HelpContent = styled.div`
  max-width: 280px;
  font-size: 12px;
  color: ${gray[700]};
  line-height: 1.5;
`

const SuggestButton = styled(Button)`
  align-self: flex-start;
`
